import { Link } from "react-router-dom";

/**
 * BrandBanner — modern geometric wordmark with a "coupon-corner" icon.
 * The icon is a stylized C-shape with a perforated edge — a subtle nod to
 * physical coupons that reads cleanly at any size. Wordmark uses a tight,
 * modern letterform pairing (semibold sans + light italic) to feel
 * simultaneously editorial and 2026.
 *
 * On hover, the icon does a subtle "clip" wink to add a moment of delight
 * without gimmicky motion. Respects prefers-reduced-motion.
 */
export default function BrandBanner({ to = "/" }) {
  const inner = (
    <span
      className="brand-banner group inline-flex items-center gap-3"
      data-testid="brand-banner"
      style={{ lineHeight: 1 }}
    >
      {/* Icon — coupon-corner mark */}
      <span className="brand-icon relative inline-block" aria-hidden="true">
        <svg
          viewBox="0 0 44 44"
          width="42"
          height="42"
          style={{ display: "block" }}
        >
          <defs>
            <linearGradient id="brand-navy" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1e3a7a" />
              <stop offset="55%" stopColor="#132a5a" />
              <stop offset="100%" stopColor="#081638" />
            </linearGradient>
          </defs>
          {/* Solid rounded navy square */}
          <rect
            x="2"
            y="2"
            width="40"
            height="40"
            rx="12"
            fill="url(#brand-navy)"
          />
          {/* Perforated edge on the right — nod to a coupon tear-line */}
          <g fill="#fff8ef">
            <circle cx="42" cy="10" r="2" />
            <circle cx="42" cy="22" r="2" />
            <circle cx="42" cy="34" r="2" />
          </g>
          {/* Stylized C in the middle — coral accent pulls from the app palette */}
          <path
            className="brand-c"
            d="M 30 14 A 8 8 0 1 0 30 30"
            fill="none"
            stroke="#ff5b48"
            strokeWidth="4"
            strokeLinecap="round"
          />
          {/* Small dot — the "clip" mark, gold for warmth */}
          <circle cx="30" cy="22" r="2.4" fill="#f7c873" />
        </svg>
      </span>

      {/* Wordmark */}
      <span
        className="brand-wordmark inline-flex flex-col leading-none select-none"
        style={{ letterSpacing: "-0.01em" }}
      >
        <span
          className="brand-name text-[1.5rem] md:text-[1.7rem] text-[#2d2a32]"
          style={{
            fontFamily: "'Georgia','Cormorant Garamond',serif",
            fontWeight: 700,
            letterSpacing: "-0.02em",
          }}
        >
          the <span style={{ fontStyle: "italic", color: "#ff5b48" }}>couponer</span>
        </span>
        <span
          className="brand-tag mt-1 text-[9px] tracking-[0.32em] text-[#5c5c5c] font-semibold"
          style={{ fontFamily: "ui-monospace, monospace" }}
        >
          CLIP · SAVE · REPEAT
        </span>
      </span>

      <style>{`
        .brand-banner .brand-c {
          transform-origin: 30px 22px;
          transition: transform 0.5s cubic-bezier(.34,1.56,.64,1);
        }
        .brand-banner:hover .brand-c {
          transform: rotate(-18deg);
        }
        .brand-icon {
          transition: transform 0.4s cubic-bezier(.34,1.56,.64,1);
        }
        .brand-banner:hover .brand-icon {
          transform: rotate(-3deg) scale(1.03);
        }
        @media (prefers-reduced-motion: reduce) {
          .brand-banner .brand-c,
          .brand-banner .brand-icon {
            transition: none !important;
            transform: none !important;
          }
        }
      `}</style>
    </span>
  );

  return to ? (
    <Link to={to} data-testid="brand-logo-link" className="inline-flex" style={{ lineHeight: 0 }}>
      {inner}
    </Link>
  ) : (
    inner
  );
}
